import axios from 'axios';
import moment from 'moment-timezone';
import { checkTotalEmailContentSizeValidator } from '../../components/sequence/shared/modals/email-modal/helpers';
import { Images } from '../app-constants';
import { MergeTagOption } from '../components/overlay-merge-tags';
import { getVariableTags } from '../components/tags-dropdown/helpers/get-variable-tags';
import toaster, { Theme } from '../toaster';
import {
  EmailContentSizeAllowed,
  generateId,
  generateRandomString,
  NumberOfAttachmentsAllowed,
  validAttachmentExtensions,
} from '../utils';
import iconsSvg from './iconSvg';
import {
  AttachmentType,
  AttachmentUploadStatus,
  TagsOptionsForInlineDropdown,
} from './types';
import { getVariableTagTooltip } from '../components/tags-dropdown/helpers/get-variable-tag-tooltip';

export const generateEditorId = (variableLength?: number) =>
  generateId({ prefix: 'editor', variableLength });

export { addAlignmentSplitButton } from './alignment-splitbutton';

export const addIcon = (editor) => {
  Object.keys(iconsSvg).forEach((key) => {
    editor.ui.registry.addIcon(key, iconsSvg[key]);
  });
};

export const getAcceptedExtensionsForAttachment = (): string =>
  validAttachmentExtensions.join(',');

export const hasValidExtension = (fileName: string): boolean =>
  new RegExp(
    `(${validAttachmentExtensions.join('|').replace(/\./g, '\\.')})$`,
    'i',
  ).test(fileName);

export const checkIsTotalAttachmentSizeIsValid = (
  attachments: AttachmentType[],
  file,
  content: string,
): boolean => {
  let attachmentsSizeInBytes: number = parseInt(file.size, 10);

  attachments.forEach((item) => {
    attachmentsSizeInBytes += parseInt(item.file.size, 10);
  });

  return !checkTotalEmailContentSizeValidator({
    content,
    attachmentsSizeInBytes,
  });
};

export const showToasterError = (msg: string) => {
  toaster.error(msg, { theme: Theme.New });
};

export const addFileToTheLocalAttachments = ({
  file,
  localAttachments,
  onLocalAttachmentChange,
  onLocalAttachmentMetaChange,
  breakLoop,
  t,
  content,
  attachmentMsg=false,
}) => {
  // Validating is attachments hard limit is exceeding `NumberOfAttachmentsAllowed` or not
  if (localAttachments.length < NumberOfAttachmentsAllowed) {
    // Validating is total size of attachments is exceeding `EmailContentSizeAllowed` or not
    if (checkIsTotalAttachmentSizeIsValid(localAttachments, file, content)) {
      // Validating is attachment has a supported file extension or not
      if (hasValidExtension(file.name)) {
        // Generating random attachment identifier for a key to link up the attachment status
        const attachmentIdentifier = generateRandomString(6);

        // Creating a new attachment data object
        const attachment = {
          attachmentIdentifier,
          file,
          status: AttachmentUploadStatus.Upload,
          attachmentSize: file.size,
        };

        // Creating a new attachment meta data object
        const attachmentsMeta = {
          percentage: 0,
          attachmentIdentifier,
          source: axios.CancelToken.source(),
        };

        // Storing the attachment and its meta in local attachment array
        onLocalAttachmentChange(attachment);
        onLocalAttachmentMetaChange(attachmentsMeta);
      } else {
        // Show error as attachment does not have valid extension
        showToasterError(t('messages.unsupported_file_type'));
      }
    } else {
      // Show error as attachments total size is exceeding the limit `EmailContentSizeAllowed`
      const sizeInMb = Math.round(EmailContentSizeAllowed / 1024 / 1000);
      const errorMsg = attachmentMsg ? 'Your attachment size should not exceed' : t('messages.email_content_size_exceeds');
      showToasterError(
        `${errorMsg} ${sizeInMb} MB.`,
      );
      breakLoop();
    }
  } else {
    // Show error as attachments count exceeding the limit `NumberOfAttachmentsAllowed`
    showToasterError(
      `${t(
        'messages.number_of_attachments_allowed_1',
      )} ${NumberOfAttachmentsAllowed} ${t(
        'messages.number_of_attachments_allowed_2',
      )}`,
    );
    breakLoop();
  }
};

const formatMergeTags = (tags) =>
  tags?.map((tag) => ({
    text: tag?.label,
    value: tag?.tag?.slice(1),
    type: 'merge tag',
    fieldType: tag.fieldType || 'text',
    isAiGenerated: tag.isAiGenerated || false,
  })) || [];

const formatVariableTags = () =>
  getVariableTags().flatMap((category) =>
    category.tags.map((tagObj) => ({
      text: tagObj.tag,
      value: tagObj.tag.slice(1),
      type: 'variable tag',
    })),
  );

export const getTagsOptionsForInlineDropdown = (
  mergeTags: MergeTagOption[],
  showVariableTags = true,
): TagsOptionsForInlineDropdown => [
  ...formatMergeTags(mergeTags),
  ...(showVariableTags ? formatVariableTags() : []),
];

export const insertChipForMergeTag = (
  mergeTag: string,
  uuid?: string,
  isVariableTag = false,
  needTrailingSpace = true,
  fieldType?: string,
) => {
  const strippedMergeTag = mergeTag?.replace(/^{{|}}$/g, '');
  const hasFallback = strippedMergeTag.includes('|'); // ** Update this Condition with more accurate check

  // ** Need to handle case where user manully put the fallback in single quotes and variable tag in single quotes
  const [tag, fallback] = hasFallback
    ? strippedMergeTag
        ?.split('|')
        ?.map((item) => item?.replace(/^'([\s\S]*)'$/, '$1')?.trim())
    : [strippedMergeTag?.trim(), ''];


  if (isVariableTag) {
    return `<span id="${uuid}" contenteditable="false" class="variable-tag-chip-container"><span class="chip-cancel-icon"><img src="${Images.ChipCancel}" alt="Chip Cancel" /></span><span class="variable-tag-chip" contenteditable="false">${tag || ''}</span></span>${needTrailingSpace ? ' ' : ''}`;
  }

  const normalizedFieldType = fieldType || '';
  const isLongTextFieldType = normalizedFieldType === 'long-text';
  const escapeHtml = (value: string) =>
    (value || '')
      ?.replace(/&/g, '&amp;')
      ?.replace(/</g, '&lt;')
      ?.replace(/>/g, '&gt;')
      ?.replace(/"/g, '&quot;')
      ?.replace(/'/g, '&#39;');

  const decodeHtmlEntities = (value: string) =>
    (value || '')
      ?.replace(/&lt;/g, '<')
      ?.replace(/&gt;/g, '>');

  const fallbackRaw =
    isLongTextFieldType && fallback
      ? decodeHtmlEntities(fallback)
      : fallback || '';
  // const needsEllipsis = isLongTextFieldType && fallbackRaw.length > 10;
  // const fallbackDisplayRaw = isLongTextFieldType
  //   ? needsEllipsis
  //     ? `${fallbackRaw.slice(0, 10)}...`
  //     : fallbackRaw
  //   : fallbackRaw;
  const fallbackId = fallback ? '' : `fallback-tooltip-${uuid}`;
  const contentId = `content-tooltip-${uuid}`;
  const tooltipElement = fallback
    ? ''
    : `<span id="${fallbackId}" class="tooltip-text" role="tooltip">Add fallback text</span>`;
  // const contentTooltipElement = `<span id="${contentId}" class="tooltip-text content-tooltip" role="tooltip">${
  //   tag || ''
  // }</span>`;

  const fallbackContent = escapeHtml(fallbackRaw);
const chipFieldTypeAttr = normalizedFieldType
    ? ` data-field-type="${normalizedFieldType}"`
    : '';
  const chipLongTextAttr =
    isLongTextFieldType && fallbackRaw
      ? ` data-long-text-fallback="${escapeHtml(fallbackRaw)}"`
      : '';
  const fallbackFieldTypeAttr = normalizedFieldType
    ? ` data-field-type="${normalizedFieldType}"`
    : '';
  const fallbackRawAttr =
    fallback !== undefined && fallback !== null && fallbackRaw !== ''
      ? ` data-raw-fallback="${escapeHtml(fallbackRaw)}"`
      : '';

  return `<span id="${uuid}" contenteditable="false" class="merge-tag-chip-container ${isLongTextFieldType ? 'long-text-merge-tag-chip-container' : ''}"${chipFieldTypeAttr}${chipLongTextAttr}><span class="chip-cancel-icon"><img src="${Images.ChipCancel}" alt="Chip Cancel" /></span><span class="merge-tag-chip" contenteditable="false" aria-describedby="${contentId}">${tag || ''}</span><span class="editable-fallback${fallback ? '' : ' default-fallback'}${isLongTextFieldType ? ' long-text-editable-fallback' : ''}" contenteditable="false"${fallbackId ? ` aria-describedby="${fallbackId}"` : ''}${fallbackFieldTypeAttr}${fallbackRawAttr}>${fallbackContent || ''}</span>${tooltipElement}</span>${needTrailingSpace ? ' ' : ''}`;
};

// Set caret position immediately after a chip identified by its UUID
export const setCaretAfterChip = (editor: any, chipId: string) => {
  try {
    if (!editor?.dom || !chipId) return;
    // Defer until TinyMCE updates DOM after insertContent
    setTimeout(() => {
      const chipEl = editor.dom.get(chipId);
      if (!chipEl) return;
      const range = editor.dom.createRng();
      const next = chipEl.nextSibling as ChildNode | null;
      if (next && next.nodeType === 3) {
        const textNode = (next as unknown) as Text;
        const len = (textNode.textContent || '').length;
        range.setStart(textNode, len);
        range.setEnd(textNode, len);
      } else {
        range.setStartAfter(chipEl);
        range.setEndAfter(chipEl);
      }
      editor.selection.setRng(range);
    }, 0);
  } catch (e) {
    // no-op
  }
};

export const removeOuterDiv = (str: string) =>
  str?.replace(/^\s*<div[^>]*>([\s\S]*)<\/div>\s*$/, '$1');

/**
 * List of supported date tag types
 * These are the exact tag names that can be inserted
 */
export const DATE_TAG_TYPES = [
  'CurrentDate - N',
  'CurrentDate + N',
  'NextWorkingDate + N',
  'NextWorkingDate - N',
  'CurrentMonth - N',
  'CurrentMonth + N',
  'CurrentYear + N',
  'CurrentYear - N',
  'DaysLeftIn / DDMMYYYY',
] as const;

const DATE_TAG_DEFAULT_OFFSETS: Partial<
  Record<(typeof DATE_TAG_TYPES)[number], number>
> = {
  'CurrentDate - N': -3,
  'CurrentDate + N': 3,
  'NextWorkingDate + N': 3,
  'NextWorkingDate - N': -3,
  'CurrentMonth - N': -2,
  'CurrentMonth + N': 2,
  'CurrentYear + N': 3,
  'CurrentYear - N': -3,
  'DaysLeftIn / DDMMYYYY': 0,
};

export const getDateTagPrompt = (
  tagType: string,
  options?: {
    offset?: number;
    timezone?: string;
    currentTime?: moment.Moment;
  },
): string => {
  if (!tagType) {
    return 'Select a date tag to see instructions.';
  }

  const normalizedTagType = DATE_TAG_TYPES?.find(
    (type) => type?.toLowerCase() === tagType?.toLowerCase(),
  );

  if (!normalizedTagType) {
    return 'Inserts a date value based on the selected tag.';
  }

  const tagWithBraces = `{{${normalizedTagType}}}`;
  const timezone =
    options?.timezone ||
    (typeof moment.tz === 'function' ? moment.tz.guess() : '');
  const currentTime = options?.currentTime || moment();
  const offset =
    options?.offset ??
    DATE_TAG_DEFAULT_OFFSETS[normalizedTagType] ??
    0;

  const tooltip = getVariableTagTooltip(
    tagWithBraces,
    currentTime,
    offset,
    timezone,
  );

  if (tooltip) {
    return tooltip;
  }

  return 'Inserts a date value based on the selected tag.';
};

export const DATE_TAG_INPUT_LABELS: Record<
  (typeof DATE_TAG_TYPES)[number],
  string
> = {
  'CurrentDate - N': 'Insert Number of Days',
  'CurrentDate + N': 'Insert Number of Days',
  'NextWorkingDate + N': 'Insert Number of Working Days',
  'NextWorkingDate - N': 'Insert Number of Working Days',
  'CurrentMonth - N': 'Insert Number of Months',
  'CurrentMonth + N': 'Insert Number of Months',
  'CurrentYear + N': 'Insert Number of Years',
  'CurrentYear - N': 'Insert Number of Years',
  'DaysLeftIn / DDMMYYYY': 'Select Date',
};

export const getDateTagInputLabel = (tagType: string): string => {
  if (!tagType) {
    return 'Add Value';
  }

  const normalizedTagType = DATE_TAG_TYPES?.find(
    (type) => type?.toLowerCase() === tagType?.toLowerCase(),
  );

  if (!normalizedTagType) {
    return 'Add Value';
  }

  return DATE_TAG_INPUT_LABELS[normalizedTagType];
};


export const dateChipTags = (
  tagType: string,
  excludeDaysLeft?: boolean,
  withBraces?: boolean
) => {
  const allTags = excludeDaysLeft
    ? DATE_TAG_TYPES?.filter((tag) => tag !== 'DaysLeftIn / DDMMYYYY')
    : DATE_TAG_TYPES;

  const formattedTags =
    withBraces === false
      ? allTags
      : allTags?.map((tag) => `{{${tag}}}`);

  return formattedTags?.some(
    (tag) => tag?.toLowerCase() === tagType?.toLowerCase()
  );
};



/**
 * Checks if a tag is a date-related tag
 * Recognizes both template format (CurrentDate - N) and actual format (CurrentDate - 2)
 */
export const isDateTag = (tag: string): boolean => {
  const strippedTag =
    tag
      ?.replace(/^{{|}}$/g, '')
      ?.split('|')?.[0]
      ?.trim() || '';
  
  // Normalize whitespace and case for comparison
  const normalizeTag = (str: string) => str?.toLowerCase()?.replace(/\s+/g, ' ')?.trim();
  const normalizedStrippedTag = normalizeTag(strippedTag);
  
  // Check if tag matches one of the date tag template types (case-insensitive and whitespace-normalized)
  const matchesTemplate = DATE_TAG_TYPES?.some(
    (dateTagType) => normalizeTag(dateTagType) === normalizedStrippedTag
  );
  
  if (matchesTemplate) {
    return true;
  }
  // Also check if it matches the actual format (with numbers instead of N)
  // E.g., "CurrentDate - 5", "CurrentDate + 10", "NextWorkingDate - 3"
  const dateTagWithNumberPatterns = [
    /^CurrentDate\s*[-+]\s*\d+$/i,      
    /^NextWorkingDate\s*[-+]\s*\d+$/i,  
    /^CurrentMonth\s*[-+]\s*\d+$/i,     
    /^CurrentYear\s*[-+]\s*\d+$/i,      
    /^DaysLeftIn\s*\/\s*.+$/i,          
  ];
  
  return dateTagWithNumberPatterns?.some((pattern) => pattern?.test(strippedTag));
};

/**
 * Extracts plain text from chip HTML, converting chips back to their original tag format
 * @param chipHtml - HTML string containing chips
 * @returns Plain text representation of the chips
 */
export const extractPlainTextFromChipHtml = (chipHtml: string): string => {
  if (!chipHtml) return '';

  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = chipHtml;

  // Find all chip containers and convert them back to original format
  const mergeTagContainers = tempDiv.querySelectorAll(
    '.merge-tag-chip-container',
  );
  mergeTagContainers.forEach((container) => {
    const mergeTagChip = container.querySelector(
      '.merge-tag-chip',
    ) as HTMLElement;
    const editableFallback = container.querySelector(
      '.editable-fallback',
    ) as HTMLElement;

    if (mergeTagChip || editableFallback) {
      const tag = mergeTagChip?.textContent || '';
      const fallback = editableFallback?.textContent || '';

      let mergeTagString = `{{${tag}`;
      if (fallback && fallback.trim() !== '') {
        mergeTagString += `|'${fallback}'`;
      }
      mergeTagString += '}}';

      const textNode = document.createTextNode(mergeTagString);
      container.parentNode?.replaceChild(textNode, container);
    }
  });

  // Handle variable tag chips
  const variableTagContainers = tempDiv.querySelectorAll(
    '.variable-tag-chip-container',
  );
  variableTagContainers.forEach((container) => {
    const variableTagChip = container.querySelector(
      '.variable-tag-chip',
    ) as HTMLElement;
    if (variableTagChip) {
      const variableTagString = `{{${variableTagChip.textContent?.trim()}}}`;
      const textNode = document.createTextNode(variableTagString);
      container.parentNode?.replaceChild(textNode, container);
    }
  });

  // Handle error tag chips
  const errorTagContainers = tempDiv.querySelectorAll('.error-chip-container');
  errorTagContainers.forEach((container) => {
    const errorTagChip = container.querySelector('.error-chip') as HTMLElement;
    if (errorTagChip) {
      const errorTagString = `{{${errorTagChip.textContent?.trim()}}}`;
      const textNode = document.createTextNode(errorTagString);
      container.parentNode?.replaceChild(textNode, container);
    }
  });

  // Handle date tag chips
  const dateTagContainers = tempDiv.querySelectorAll(
    '.date-tag-chip-container',
  );
  dateTagContainers.forEach((container) => {
    // Get the original tag type from data attribute
    const tagType = container.getAttribute('data-tag-type') || '';
    const editableFallback = container.querySelector(
      '.editable-fallback',
    ) as HTMLElement;
    const value = editableFallback?.textContent?.trim() || '';

    // Reconstruct: {{CurrentDate - N|'5'}}
    let dateTagString = `{{${tagType}`;
    if (value) {
      dateTagString += `|'${value}'`;
    }
    dateTagString += '}}';

    const textNode = document.createTextNode(dateTagString);
    container.parentNode?.replaceChild(textNode, container);
  });

  return tempDiv.textContent || tempDiv.innerText || '';
};

export const convertSpinsToChips = (spins: string[]) =>
  spins?.map((spin, index) => {
    const firstSpin = index === 0;
    const spinHasTags =
      spin?.includes('merge-tag-chip-container') ||
      spin?.includes('variable-tag-chip-container') ||
      spin?.includes('date-tag-chip-container') ||
      spin?.includes('error-chip-container');

    const formattedSpin = removeOuterDiv(spin);
    const chipId = generateRandomString(6);
    const tooltipId = `spin-chip-tooltip-${chipId}`;

    // Extract plain text for tooltip to avoid showing chip HTML
    const plainTextForTooltip = extractPlainTextFromChipHtml(formattedSpin);

    const spinHtml = `${firstSpin ? '' : '<span class="spin-divider"></span>'}<span class="spin-tag-chip ${spinHasTags ? 'with-merge-tag' : ''}" contenteditable="false" aria-describedby="${tooltipId}">${formattedSpin}</span>`;
    return spinHtml;
  });

export const insertChipForSpinTag = (
  spinTag: string,
  uuid?: string,
  needTrailingSpace = true,
) => {
  const spins = spinTag
    ?.replace('{spin}', '')
    ?.replace('{endspin}', '')
    ?.split('|');

  const spinTagHtml = convertSpinsToChips(spins);

  const spinTooltipId = `spin-tooltip-${uuid}`;

  return `<span id="${uuid}" contenteditable="false" class="spin-tag-chip-container"><span class="spin-start"></span><span class="spin-chips">${spinTagHtml?.join('')}</span><span class="add-spin-icon" aria-describedby="${spinTooltipId}"><span class="chip-cancel-icon"><img src="${Images.ChipCancel}" alt="Chip Cancel" /></span></span><span id="${spinTooltipId}" class="tooltip-text" role="tooltip">Add Spintax</span></span>${needTrailingSpace ? ' ' : ''}`;
};

/**
 * Gets the default value for a date tag based on its type
 */
const getDefaultDateTagValue = (tagType: string): string => {
  if (
    dateChipTags(tagType, true, false)
  ) {
    return '0';
  }
  if (tagType === 'DaysLeftIn / DDMMYYYY') {
    // Return today's date in DD/MM/YYYY format
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  }
  return '';
};

/**
 * Formats date value for display in chip
 * Converts 03112025 (8 digits without slashes) to 03/11/2025 (with slashes for UI)
 */
const formatDateValueForDisplay = (value: string): string => {
  // If it's 8 digits without slashes, format as DD/MM/YYYY
  if (/^\d{8}$/?.test(value)) {
    return `${value.substring(0, 2)}/${value.substring(2, 4)}/${value.substring(4, 8)}`;
  }
  // Otherwise return as-is
  return value;
};

 // Normalize to proper casing
  const properCasingMap: Record<string, string> = {
        'currentdate': 'CurrentDate',
        'nextworkingdate': 'NextWorkingDate',
        'currentmonth': 'CurrentMonth',
        'currentyear': 'CurrentYear',
      };

/**
 * Inserts a date tag chip for date-related tags
 */
export const insertChipForDateTag = (
  dateTag: string,
  uuid?: string,
  needTrailingSpace = true,
) => {
  const strippedDateTag = dateTag?.replace(/^{{|}}$/g, '');
  const hasFallback = strippedDateTag.includes('|');

  // Extract tag type and existing value (if any)
  let [tagType, existingValue] = hasFallback
    ? strippedDateTag
        ?.split('|')
        ?.map((item) => item?.replace(/^'([\s\S]*)'$/, '$1')?.trim())
    : [strippedDateTag, ''];

  // Check if tagType contains actual number instead of 'N' (e.g., "CurrentDate - 5")
  // and convert it to template format with extracted value
  if (
    /^CurrentDate\s*[-+]\s*\d+$/i.test(tagType) ||
    /^NextWorkingDate\s*[-+]\s*\d+$/i.test(tagType) ||
    /^CurrentMonth\s*[-+]\s*\d+$/i.test(tagType) ||
    /^CurrentYear\s*[-+]\s*\d+$/i.test(tagType)
  ) {
    // Extract the number from the tag
    const match = tagType.match(/(CurrentDate|NextWorkingDate|CurrentMonth|CurrentYear)\s*([-+])\s*(\d+)/i);
    if (match) {
      const [, tagName, operator, number] = match;
      const properTagName = properCasingMap[tagName?.toLowerCase()] || tagName;
      tagType = `${properTagName} ${operator} N`;
      existingValue = existingValue || number;
    }
  } else if (/^DaysLeftIn\s*\/\s*.+$/i.test(tagType)) {
    // For DaysLeftIn with actual date: "DaysLeftIn / 31/12/2024"
    const match = tagType.match(/DaysLeftIn\s*\/\s*(.+)/i);
    if (match) {
      const dateValue = match[1]?.trim();
      // Check if the value is the placeholder "DDMMYYYY" (case-insensitive)
      // If so, don't use it as existingValue, let it default to today's date
      if (dateValue && !/^DDMMYYYY$/i.test(dateValue)) {
        existingValue = existingValue || dateValue;
      }
      tagType = 'DaysLeftIn / DDMMYYYY';
    }
  } else {
    // Normalize tagType for cases without numbers (e.g., "CurrentDate + N", "CurrentDate - N")
    const simpleMatch = tagType.match(/^(CurrentDate|NextWorkingDate|CurrentMonth|CurrentYear)\s*([-+])\s*N$/i);
    if (simpleMatch) {
      const [, tagName, operator] = simpleMatch;
      // Normalize to proper casing (capitalize first letter of each word)
      const normalizedTagName = tagName?.charAt(0)?.toUpperCase() + tagName?.slice(1)?.toLowerCase();
      const properTagName = properCasingMap[tagName?.toLowerCase()] || normalizedTagName;
      tagType = `${properTagName} ${operator} N`;
    } else if (/^DaysLeftIn\s*\/\s*DDMMYYYY$/i.test(tagType)) {
      // Normalize DaysLeftIn / DDMMYYYY (case-insensitive)
      tagType = 'DaysLeftIn / DDMMYYYY';
    }
  }

  // Get the display name (e.g., "CurrentDate -" from "CurrentDate - N")
  const displayName = tagType?.replace(/([+-])\s*.*$|\/\s*.*$/g, '$1')?.trim();

  
  // Use existing value or default
  const value = existingValue || getDefaultDateTagValue(tagType);
  const displayValue = formatDateValueForDisplay(value);
  
  // For DaysLeftIn, always show the actual date value (don't use CSS default)
  const isDaysLeftIn = tagType === 'DaysLeftIn / DDMMYYYY';
  
  // Check if we should show default (empty content with CSS "0") or actual value
  // Only use CSS default for numeric date tags (not DaysLeftIn)
  const hasCustomValue = existingValue && existingValue !== '0' && existingValue !== getDefaultDateTagValue(tagType);
  const isDefaultValue = !isDaysLeftIn && !hasCustomValue;

  // Only show tooltip for numeric date tags with default value, not for DaysLeftIn
  const fallbackId = isDefaultValue ? `fallback-tooltip-${uuid}` : '';
  const contentId = `content-tooltip-${uuid}`;
  const tooltipElement = isDefaultValue ? `<span id="${fallbackId}" class="tooltip-text" role="tooltip">Add Variable Value</span>` : '';

  return `<span id="${uuid}" contenteditable="false" class="date-tag-chip-container" data-tag-type="${tagType}"><span class="chip-cancel-icon"><img src="${Images.ChipCancel}" alt="Chip Cancel" /></span><span class="date-tag-chip" contenteditable="false" aria-describedby="${contentId}">${displayName}</span><span class="editable-fallback${isDefaultValue ? ' default-fallback' : ''}" contenteditable="false"${fallbackId ? ` aria-describedby="${fallbackId}"` : ''}>${isDefaultValue ? '' : displayValue}</span>${tooltipElement}</span>${needTrailingSpace ? ' ' : ''}`;
};

/**
 * Inserts an error chip for unknown/invalid tags
 */
export const insertErrorChip = (errorTag: string, uuid?: string) => {
  const strippedErrorTag = errorTag?.replace(/^{{|}}$/g, '');
  return `<span id="${uuid}" contenteditable="false" title="Invalid tag {{${strippedErrorTag}}}" class="error-chip-container"><span class="chip-cancel-icon"><img src="${Images.ChipCancel}" alt="Chip Cancel" /></span><span class="error-chip-icon"></span><span class="error-chip" contenteditable="false">${strippedErrorTag}</span></span>`;
};

// Split spintax options while respecting merge tags like {{Tag|fallback}}
// Only split on '|' characters that are not inside double curly braces
const splitSpintaxOptionsRespectingMerges = (input: string): string[] => {
  const options: string[] = [];
  let buffer = '';
  let i = 0;
  let mergeDepth = 0; // counts nesting of '{{' .. '}}' (practically 0 or 1)

  while (i < input.length) {
    const char = input[i];
    const next = input[i + 1];

    if (char === '{' && next === '{') {
      // Enter merge tag
      mergeDepth += 1;
      buffer += '{{';
      i += 2;
    } else if (char === '}' && next === '}' && mergeDepth > 0) {
      // Exit merge tag
      mergeDepth -= 1;
      buffer += '}}';
      i += 2;
    } else if (char === '|' && mergeDepth === 0) {
      // Split only when not inside merge tag
      options.push(buffer);
      buffer = '';
      i += 1;
    } else {
      buffer += char;
      i += 1;
    }
  }

  options.push(buffer);
  return options;
};

// Replace merge tags inside plain text with corresponding chips
const convertMergeTagsInTextToChips = (
  text: string,
  allTagsOptions: TagsOptionsForInlineDropdown = [],
  treatMergeTagAsVariableTag = false,
): string => {
  const mergeTagRegex = /(\{{2}[^\}\{]+\}{2})/g;
  let lastIndex = 0;
  let result = '';
  let match: RegExpExecArray | null = mergeTagRegex.exec(text);

  // Check if variable tags are allowed (date tags are variable tags)
  const hasVariableTags = allTagsOptions?.some((tag) => tag.type === 'variable tag');

  while (match !== null) {
    const fullMatch = match[0];
    const formattedMatch = match[1]?.replace(/^{{|}}$/g, '');
    const rawTagValue = formattedMatch?.split('|')?.[0] || '';
    const tagValue = rawTagValue.trim().toLowerCase();

    // Add preceding text
    if (match.index > lastIndex) {
      result += text.slice(lastIndex, match.index);
    }

    if (isDateTag(fullMatch)) {
      // If variable tags are not allowed, date tags should be treated as errors
      if (!hasVariableTags) {
        result += insertErrorChip(fullMatch, generateRandomString(6));
      } else {
        result += insertChipForDateTag(
          fullMatch,
          generateRandomString(6),
          false,
        );
      }
    } else {
      // Normalize input tag value: preserve spaces but make case-insensitive
      const normalizedTarget = `{{${tagValue}}}`.toLowerCase();
      const tagOption = allTagsOptions?.find(
        (tag) =>
          (tag.text || '').toLowerCase() === normalizedTarget,
      );

      if (!tagOption) {
        result += insertErrorChip(fullMatch, generateRandomString(6));
      } else {
        const isVariableTag =
          tagOption?.type === 'variable tag' || treatMergeTagAsVariableTag;

        // Extract fallback from user's input (if any)
        const fallbackPart = formattedMatch?.includes('|')
          ? formattedMatch?.split('|')?.slice(1)?.join('|')
          : '';

        // Reconstruct with properly cased tag from tagOption and preserve fallback
        const properlyCasedTag = tagOption.text?.replace(/^{{|}}$/g, '') || tagOption?.value;
        const reconstructedTag = fallbackPart
          ? `{{${properlyCasedTag}|${fallbackPart}}}`
          : `{{${properlyCasedTag}}}`;

        result += insertChipForMergeTag(
          reconstructedTag,
          generateRandomString(6),
          isVariableTag,
          false,
          tagOption?.fieldType,
        );
      }
    }

    lastIndex = match.index + fullMatch.length;
    match = mergeTagRegex.exec(text);
  }

  if (lastIndex < text.length) {
    result += text.slice(lastIndex);
  }

  return result;
};

// Build spin chip HTML where each option can include merge chips
const buildSpinChipContainerHtmlFromInner = (
  innerContent: string,
  uuid: string,
  allTagsOptions: TagsOptionsForInlineDropdown = [],
  needTrailingSpace = true,
): string => {
  const options = splitSpintaxOptionsRespectingMerges(innerContent);

  const optionHtmlList = options?.map((opt, index) => {
    // TODO: Move this logic to common logic & reuse existing logic if applied somewhere else
    const regex = /\{\{(.*?)\}\}/g;
    const hasMergeTags = opt?.match(regex);
    const first = index === 0;
    const converted = convertMergeTagsInTextToChips(opt, allTagsOptions);
    const chipId = generateRandomString(6);
    const tooltipId = `spin-chip-tooltip-${chipId}`;

    // Use original option text for tooltip to show merge tags in original format
    const plainTextForTooltip = opt;

    return `${first ? '' : '<span class="spin-divider"></span>'}<span class="spin-tag-chip ${hasMergeTags ? 'with-merge-tag' : ''}" contenteditable="false" aria-describedby="${tooltipId}">${converted}</span>${/* <span id="${tooltipId}" class="tooltip-text spin-chip-tooltip" role="tooltip">${plainTextForTooltip}</span> */ ''}`;
  });

  const spinTooltipId = `spin-tooltip-${uuid}`;

  return `<span id="${uuid}" contenteditable="false" class="spin-tag-chip-container"><span class="spin-start"></span><span class="spin-chips">${optionHtmlList.join('')}</span><span class="add-spin-icon" aria-describedby="${spinTooltipId}"><span class="chip-cancel-icon"><img src="${Images.ChipCancel}" alt="Chip Cancel" /></span></span><span id="${spinTooltipId}" class="tooltip-text" role="tooltip">Add Spintax</span></span>${needTrailingSpace ? ' ' : ''}`;
};

export const makeMergeTagEditable = (
  element: HTMLElement,
  openMergeTagModal?: (mergeTagId: string) => void,
) => {
  const elementId = element?.id || '';

  if (openMergeTagModal) {
    openMergeTagModal(elementId);
  }
};

export const makeSpinTagEditable = (
  element: HTMLElement,
  openSpinTagModal?: (spinTagId: string) => void,
) => {
  const elementId = element?.id || '';

  if (openSpinTagModal) {
    openSpinTagModal(elementId);
  }
};

export const makeDateTagEditable = (
  element: HTMLElement,
  openDateTagModal?: (dateTagId: string) => void,
) => {
  const elementId = element?.id || '';

  if (openDateTagModal) {
    openDateTagModal(elementId);
  }
};

// duplicate removed; see earlier definition of insertErrorChip

/**
 * Converts HTML with merge tag chips back to original merge tag format
 * @param html - HTML string containing merge tag chips
 * @returns HTML string with merge tag chips converted to {{tag}} or {{tag|'fallback'}} format
 */
export const stripMergeTagHtml = (html: string) => {
  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Find all merge tag chip containers
  const mergeTagContainers = tempDiv?.querySelectorAll(
    '.merge-tag-chip-container',
  );

  mergeTagContainers?.forEach((container) => {
    const mergeTagChip = container?.querySelector(
      '.merge-tag-chip',
    ) as HTMLElement;
    const editableFallback = container?.querySelector(
      '.editable-fallback',
    ) as HTMLElement;

    if (mergeTagChip || editableFallback) {
      const tag = mergeTagChip?.textContent || '';
      let fallback = editableFallback?.textContent || '';
      const isLongTextFieldType = container.classList.contains(
        'long-text-merge-tag-chip-container',
      );
      const isInsideSpinTag = Boolean(
        container.closest('.spin-tag-chip-container'),
      );
      if (isLongTextFieldType || isInsideSpinTag) {
        fallback = fallback
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
      }

      // Create the merge tag string
      let mergeTagString = `{{${tag}`;
      if (fallback && fallback.trim() !== '') {
        mergeTagString += `|'${fallback}'`;
      }
      mergeTagString += '}}';

      // Replace the container with the merge tag string
      const textNode = document.createTextNode(mergeTagString);
      container.parentNode?.replaceChild(textNode, container);
    }
  });

  return tempDiv.innerHTML;
};

/**
 * Converts HTML with date tag chips back to original date tag format
 * @param html - HTML string containing date tag chips
 * @returns HTML string with date tag chips converted to {{tag}} or {{tag|'value'}} format
 * Note: If value is '0', no fallback is added (behaves like merge tags with no fallback)
 */
export const stripDateTagHtml = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Find all date tag chip containers
  const dateTagContainers = tempDiv?.querySelectorAll(
    '.date-tag-chip-container',
  );

  dateTagContainers?.forEach((container) => {
    // Get the original tag type from data attribute
    const tagType = container.getAttribute('data-tag-type') || '';
    const editableFallback = container?.querySelector(
      '.editable-fallback',
    ) as HTMLElement;
    const value = editableFallback?.textContent?.trim() || '';

    // Reconstruct the tag: {{CurrentDate - N|'5'}}
    // If value is '0', don't add fallback (similar to merge tag with no fallback)
    let dateTagString = '';
    // Convert from our chip format to the format existing code expects
    if (tagType !== 'DaysLeftIn / DDMMYYYY') {
      // For date math tags, replace 'N' with actual value
      const actualValue = value || '0';
      dateTagString = `{{${tagType?.replace(/ N/i, ` ${actualValue}`)}}}`;
    } else if (tagType === 'DaysLeftIn / DDMMYYYY') {
      // For DaysLeftIn, remove slashes from date for backend (03/11/2025 → 03112025)
      const actualValue = value || '';
      const valueWithoutSlashes = actualValue?.replace(/\//g, '');
      dateTagString = `{{DaysLeftIn / ${valueWithoutSlashes}}}`;
    }

    // Replace the container with the date tag string
    const textNode = document.createTextNode(dateTagString);
    container.parentNode?.replaceChild(textNode, container);
  });
  return tempDiv.innerHTML;
};

/**
 * Converts HTML with variable tag chips back to original variable tag format
 * @param html - HTML string containing variable tag chips
 * @returns HTML string with variable tag chips converted to {{tag}} format
 */
export const stripVariableTagHtml = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const variableTagContainers = tempDiv?.querySelectorAll(
    '.variable-tag-chip-container',
  );

  variableTagContainers?.forEach((container) => {
    const variableTagChip = container?.querySelector(
      '.variable-tag-chip',
    ) as HTMLElement;
    if (variableTagChip) {
      const variableTagString = `{{${variableTagChip?.textContent?.trim()}}}`;
      const textNode = document.createTextNode(variableTagString);
      container?.parentNode?.replaceChild(textNode, container);
    }
  });

  return tempDiv.innerHTML;
};

/**
 * Converts HTML with error tag chips back to original error tag format
 * @param html - HTML string containing error tag chips
 * @returns HTML string with error tag chips converted to {{tag}} format
 */
export const stripErrorTagHtml = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  const errorTagContainers = tempDiv?.querySelectorAll('.error-chip-container');

  errorTagContainers?.forEach((container) => {
    const errorTagChip = container?.querySelector('.error-chip') as HTMLElement;
    if (errorTagChip) {
      const errorTagString = `{{${errorTagChip?.textContent?.trim()}}}`;
      const textNode = document.createTextNode(errorTagString);
      container?.parentNode?.replaceChild(textNode, container);
    }
  });

  return tempDiv.innerHTML;
};

/**
 * Converts HTML with spin tag chips back to original spin tag format
 * @param html - HTML string containing spin tag chips
 * @returns HTML string with spin tag chips converted to {spin}{spinTagChips}{endspin} format
 */
export const stripSpinTagHtml = (html: string) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Find all spin tag chip containers
  const spinTagContainers = tempDiv?.querySelectorAll(
    '.spin-tag-chip-container',
  );

  spinTagContainers?.forEach((container) => {
    const spinTagChips =
      ((container?.querySelectorAll(
        '.spin-tag-chip',
      ) as unknown) as NodeListOf<Element>) || [];

    const spinTagChipsText = Array.from(spinTagChips)
      ?.map((chip) => ` ${chip?.textContent?.trim()} `)
      ?.filter((chip) => chip !== '')
      .join('|');

    const spinTagString = `{spin}${spinTagChipsText}{endspin}`;

    const textNode = document.createTextNode(spinTagString);
    container?.parentNode?.replaceChild(textNode, container);
  });

  return tempDiv.innerHTML;
};

/**
 * Converts HTML with all tag chips back to original tag format
 * @param html - HTML string containing all tag chips
 * @returns HTML string with all tag chips converted to original tag format
 */
export const stripChipTagsFromHtml = (html: string) =>
  stripSpinTagHtml(
    stripVariableTagHtml(
      stripMergeTagHtml(
        stripDateTagHtml(stripErrorTagHtml(html)),
      ),
    ),
  );

/**
 * Saves the current cursor position in the editor
 * @param editor - TinyMCE editor instance
 * @returns Bookmark object or null if no selection
 */
export const saveCursorPosition = (editor: any) => {
  if (!editor || !editor.selection) {
    return null;
  }

  try {
    const bookmark = editor.selection.getBookmark(2, true);

    return bookmark;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to save cursor position:', error);
    return null;
  }
};

/**
 * Restores the cursor position in the editor
 * @param editor - TinyMCE editor instance
 * @param bookmark - Bookmark object from saveCursorPosition
 */
export const restoreCursorPosition = (editor: any, bookmark: any) => {
  if (!editor || !editor.selection || !bookmark) {
    return;
  }

  try {
    editor.selection.moveToBookmark(bookmark);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to restore cursor position:', error);
  }
};

/**
 * Converts HTML with merge tags and spin tags to their respective chip formats
 * @param html - HTML string containing merge tag values {{tag}} or {{tag|'fallback'}} format and spin tags {spin}...{endspin} format
 * @param allTagsOptions - Available tag options for validation
 * @param convertSpinTags - Whether to convert spin tags to chips (default: true)
 * @param treatMergeTagAsVariableTag - Whether to treat merge tags as variable tags (default: false)
 * @returns HTML string with merge tag values and spin tags converted to chip format
 */
export const convertHtmlToMergeTagFormat = (
  html: string,
  allTagsOptions: TagsOptionsForInlineDropdown = [],
  convertSpinTags = true,
  treatMergeTagAsVariableTag = false,
) => {
// Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Check if variable tags are allowed (date tags are variable tags)
  const hasVariableTags = allTagsOptions?.some((tag) => tag.type === 'variable tag');

  // Find all text nodes that might contain merge tags or spin tags
  const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT);

  const textNodes: Text[] = [];
  let node = walker.nextNode();
  while (node !== null) {
    textNodes.push(node as Text);
    node = walker.nextNode();
  }

  // Process each text node to find and replace merge tags and spin tags
  textNodes.forEach((textNode) => {
    const text = textNode.textContent || '';

    // Regex to match merge tags: {{tag}} or {{tag|'fallback'}}
    const mergeTagRegex = /(\{{2}[^\}\{]+\}{2})/g;
    // const mergeTagRegex = /\{\{([^}]+(?:\|[^}]+)?)\}\}/g; // Do not remove
    // Regex to match spin tags: {spin}...{endspin}
    const spinTagRegex = /\{spin\}([^]*?)\{endspin\}/g;

    // Check if text contains either merge tags or spin tags
    const hasMergeTags = mergeTagRegex.test(text);
    const hasSpinTags = convertSpinTags && spinTagRegex.test(text);

    if (hasMergeTags || hasSpinTags) {
      // Reset regex lastIndex
      mergeTagRegex.lastIndex = 0;
      spinTagRegex.lastIndex = 0;

      // Collect all matches with their positions
      const allMatches: Array<{
        type: 'merge' | 'spin';
        match: RegExpExecArray;
        fullMatch: string;
      }> = [];

      // Collect spin tag matches first (ranges we'll respect) - only if convertSpinTags is true
      const spinMatches: Array<{
        type: 'spin';
        match: RegExpExecArray;
        fullMatch: string;
        start: number;
        end: number;
      }> = [];

      if (convertSpinTags) {
        let spinMatch = spinTagRegex.exec(text);
        while (spinMatch !== null) {
          const full = spinMatch[0];
          const start = spinMatch.index;
          const end = start + full?.length;
          spinMatches?.push({
            type: 'spin',
            match: spinMatch,
            fullMatch: full,
            start,
            end,
          });
          spinMatch = spinTagRegex.exec(text);
        }
      }

      // Collect merge tag matches only outside of spintax ranges
      const isInsideAnySpin = (idx: number) =>
        spinMatches.some((s) => idx >= s.start && idx < s.end);

      let mergeMatch = mergeTagRegex.exec(text);
      while (mergeMatch !== null) {
        const start = mergeMatch.index;
        if (!isInsideAnySpin(start)) {
          allMatches.push({
            type: 'merge',
            match: mergeMatch,
            fullMatch: mergeMatch[0],
          });
        }
        mergeMatch = mergeTagRegex.exec(text);
      }

      // Push spin matches
      spinMatches.forEach((s) => {
        allMatches.push({
          type: 'spin',
          match: s.match,
          fullMatch: s.fullMatch,
        });
      });

      // Sort matches by their position in the text
      allMatches.sort((a, b) => a.match.index - b.match.index);

      let lastIndex = 0;
      const fragment = document.createDocumentFragment();

      // Process all matches in order
      allMatches.forEach((matchData) => {
        const { type, match, fullMatch } = matchData;

        // Add text before the current tag
        if (match.index > lastIndex) {
          fragment.appendChild(
            document.createTextNode(text.slice(lastIndex, match.index)),
          );
        }

        let chipHtml = '';

        if (type === 'merge') {
          const formattedMatch = match[1]?.replace(/^{{|}}$/g, '');
          const rawTagValue = formattedMatch?.split('|')?.[0] || '';
          const tagValue = rawTagValue?.trim()?.toLowerCase();

          // Check if this is a date tag first
          if (isDateTag(fullMatch)) {
            // If variable tags are not allowed, date tags should be treated as errors
            if (!hasVariableTags) {
              chipHtml = insertErrorChip(fullMatch, generateRandomString(6));
            } else {
              chipHtml = insertChipForDateTag(
                fullMatch,
                generateRandomString(6),
                false,
              );
            }
          } else {
            // Normalize input tag value: preserve spaces but make case-insensitive
            const normalizedTarget = `{{${tagValue}}}`.toLowerCase();
            const tagOption = allTagsOptions?.find(
              (tag) =>
                (tag.text || '').toLowerCase() === normalizedTarget,
            );

            if (!tagOption) {
              chipHtml = insertErrorChip(fullMatch, generateRandomString(6));
            } else {
              const isVariableTag =
                tagOption?.type === 'variable tag' ||
                treatMergeTagAsVariableTag;

              chipHtml = insertChipForMergeTag(
                fullMatch,
                generateRandomString(6),
                isVariableTag,
                false,
                tagOption?.fieldType,
              );
            }
          }
        } else if (type === 'spin') {
          // Build spin chip including merge chips inside options
          const inner = match[1] || '';
          chipHtml = buildSpinChipContainerHtmlFromInner(
            inner,
            generateRandomString(6),
            allTagsOptions,
            false,
          );
        }

        // Create a temporary div to parse the chip HTML
        const chipDiv = document.createElement('div');
        chipDiv.innerHTML = chipHtml;

        // Move the chip element to the fragment
        while (chipDiv.firstChild) {
          fragment.appendChild(chipDiv.firstChild);
        }

        lastIndex = match.index + fullMatch.length;
      });

      // Add remaining text after the last tag
      if (lastIndex < text.length) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      // Replace the text node with the fragment
      textNode.parentNode?.replaceChild(fragment, textNode);
    }
  });

  return tempDiv.innerHTML;
};

/**
 * Converts HTML to merge tag format while preserving cursor position
 * @param html - HTML string containing merge tag values
 * @param editor - TinyMCE editor instance
 * @param allTagsOptions - Available tag options for validation
 * @param restoreCursor - Whether to restore cursor position after conversion
 * @param convertSpinTags - Whether to convert spin tags to chips (default: true)
 * @param treatMergeTagAsVariableTag - Whether to treat merge tags as variable tags (default: false)
 * @returns HTML string with merge tag values converted to chip format
 */
export const convertHtmlToMergeTagFormatWithCursorPreservation = (
  html: string,
  editor: any,
  allTagsOptions: TagsOptionsForInlineDropdown = [],
  restoreCursor = true,
  convertSpinTags = true,
  treatMergeTagAsVariableTag = false,
) => {
  // Save current cursor position
  const bookmark = saveCursorPosition(editor);

  // Convert HTML to merge tag format
  const convertedHtml = convertHtmlToMergeTagFormat(
    html,
    allTagsOptions,
    convertSpinTags,
    treatMergeTagAsVariableTag,
  );

  // If the HTML changed, update the editor content and restore cursor
  if (convertedHtml !== html && editor) {
    // Set the new content without triggering change events to prevent unwanted side effects
    // Using no_events: true prevents this programmatic change from firing onEditorChange callbacks
    editor.setContent(convertedHtml, { no_events: true });

    // Restore cursor position after a small delay to ensure DOM is updated
    if (restoreCursor) {
      setTimeout(() => {
        restoreCursorPosition(editor, bookmark);
      }, 0);
    }
  }

  return convertedHtml;
};
